/**
 * Platform back-office shell. Same frame as the restaurant back-office --
 * Espresso sidebar, top bar -- with a single destination: the restaurants.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { AuthUserDTO } from '@scan2eat/shared';

import { labels } from '../labels.js';
import { Icon } from './Icon.js';
import { Logo } from './Logo.js';
import styles from './PlatformLayout.module.css';

interface PlatformLayoutProps {
  user: AuthUserDTO;
  onSignOut: () => void;
  children: ReactNode;
}

export function PlatformLayout({ user, onSignOut, children }: PlatformLayoutProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={styles.shell}>
      <aside className={`${styles.sidebar} ${open ? styles.sidebarOpen : ''}`} aria-label={labels.nav.label}>
        <div className={styles.brand}>
          <Logo size={32} withWordmark tone="dark" />
          <button type="button" className={`btn-icon btn-icon--quiet ${styles.close}`} onClick={() => setOpen(false)} aria-label={labels.nav.closeMenu}>
            <Icon name="close" />
          </button>
        </div>
        <nav className={styles.nav}>
          <p className={styles.heading}>{labels.nav.heading}</p>
          <a href="/" className={`${styles.item} ${styles.itemActive}`} aria-current="page">
            <Icon name="store" />
            {labels.nav.restaurants}
          </a>
        </nav>
        <div className={styles.footer}>
          <div className={styles.user}>
            <span className="avatar">{`${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}</span>
            <span className={styles.userText}>
              <strong className="truncate">{user.firstName} {user.lastName}</strong>
              <span className="truncate">{user.email}</span>
            </span>
          </div>
          <button type="button" className={styles.signOut} onClick={onSignOut}>
            <Icon name="logout" />
            {labels.app.signOut}
          </button>
        </div>
      </aside>

      {open && <button type="button" className={styles.scrim} onClick={() => setOpen(false)} aria-label={labels.nav.closeMenu} />}

      <div className={styles.column}>
        <header className={styles.topbar}>
          <button type="button" className={`btn-icon btn-icon--quiet ${styles.menuButton}`} onClick={() => setOpen(true)} aria-label={labels.nav.openMenu} aria-expanded={open}>
            <Icon name="list" />
          </button>
          <span className={styles.context}>
            <strong>{labels.app.name}</strong>
            <span className="badge badge--outline">{labels.app.subtitle}</span>
          </span>
        </header>
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
